import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { PaginationResponse } from '@/types/pagination';
import { GetAllClaimsPayload } from '@/domains/claims/store/types/AllClaimsState';

export type AllClaimsState = {
  list: NormalizedV2Response[];
  counters: {
    allClaims: number;
    myClaims: number;
  };
  statuses: {
    isLoading: boolean;
  };
  pagination: PaginationResponse;
};

const initialState: AllClaimsState = {
  list: [],
  counters: {
    allClaims: 0,
    myClaims: 0,
  },
  statuses: {
    isLoading: false,
  },
  pagination: {
    numberOfItems: 0,
    first: '1',
    last: undefined,
    previous: '0',
    next: '0',
    page: 1,
    perPage: 20,
  },
};

const allClaimsListSlice = createSlice({
  name: 'allClaimsListSlice',
  initialState: initialState,
  reducers: {
    getAllClaimsList: (state: AllClaimsState, _payload: PayloadAction<GetAllClaimsPayload>) => {
      state.statuses.isLoading = true;
      state.pagination = initialState.pagination;
    },
    getAllClaimsListSuccess: (
      state: AllClaimsState,
      {
        payload: { listings, pagination, counters, shouldRewrite = true },
      }: PayloadAction<{
        listings: NormalizedV2Response[];
        counters: { allClaims?: number; myClaims?: number };
        pagination: PaginationResponse;
        shouldRewrite?: boolean;
      }>,
    ) => {
      state.statuses.isLoading = false;
      state.list = shouldRewrite ? listings : [...state.list, ...listings];
      state.pagination = pagination;
      state.counters = { ...state.counters, ...counters };
    },
    incrementAllClaimsListPage: (state: AllClaimsState, _payload: PayloadAction<GetAllClaimsPayload>) => {
      state.statuses.isLoading = true;
      state.pagination.page += 1;
    },
    updateAllClaimsCounters: () => {},
    updateAllClaimsCountersSuccess: (
      state: AllClaimsState,
      { payload }: PayloadAction<{ allClaims: number; myClaims: number }>,
    ) => {
      state.counters = payload;
    },
    resetAllClaimsListSlice: () => initialState,
  },
});

export const {
  getAllClaimsList,
  getAllClaimsListSuccess,
  updateAllClaimsCounters,
  updateAllClaimsCountersSuccess,
  incrementAllClaimsListPage,
  resetAllClaimsListSlice,
} = allClaimsListSlice.actions;

export const allClaimsListReducer = allClaimsListSlice.reducer;
export const allClaimsListReducerPath = allClaimsListSlice.reducerPath;
